<template>
  <div class="flex flex-col">
    <PensionPortfolioNavbar
      :show-back-button="false"
      :logo-props="{ to: { name: 'index' } }"
      :exit-button-props="{ to: { name: 'pension-finqfull' } }"
    />

    <div class="max-w-large relative mx-auto flex w-full flex-col md:px-5" v-auto-animate="autoAnimateFade">
      <PensionPortfolioValuePropositionHeader
        class="mt-12 max-w-3xl"
        v-bind="pageHeader"
        :is-loading="isPageLoading"
      />

      <template v-if="isNoneHappyFlow">
        <PensionPortfolioPartialsManuallyContinueProcess class="mt-16" />
        <span class="text-2.5xl/8 mt-8 font-semibold"> {{ t("pensionProductTitle") }} </span>
      </template>

      <PensionPortfolioValueProposition
        class="relative w-full"
        :is-locked="isValuePropositionLocked"
        :is-manual-continue-process="isNoneHappyFlow"
        :no-recommendations-flow="noRecommendationsFlow"
      >
      </PensionPortfolioValueProposition>
    </div>

    <div class="container">
      <PensionPortfolioExpandableRow class="mt-24 md:mb-10 md:mt-10">
        <PensionPortfolio class="mt-16" />
      </PensionPortfolioExpandableRow>
    </div>

    <template v-if="!isNoneHappyFlow">
      <div class="h-36 md:h-20"></div>

      <div class="fixed bottom-0 left-1/2 z-10 flex w-full -translate-x-1/2 border-t bg-white px-5">
        <div
          class="pointer-events-none absolute inset-x-0 bottom-28 h-16 translate-y-0 bg-gradient-to-b from-transparent from-40% to-white to-90% md:hidden"
        />
        <UiButton
          class="max-w-large mx-auto my-10 h-10 w-full md:my-4"
          variant="default"
          @click="handleRecommendationFlow"
        >
          {{ noRecommendationsFlow ? t("continue") : t("insightsDetails") }}
        </UiButton>
      </div>
    </template>

    <PensionKYCTwoFlowPopup
      v-if="kycTwoCookie && !isNoneHappyFlow && !isExclusionsLoading"
      v-model:open="isKycTwoOpen"
    />
  </div>
</template>

<script setup lang="ts">
import { FlowStage } from "@finq/app-base/types/flowStage.namespace"

import { Kyc } from "@finq/kyc/composables/kyc-namespace"
import { PopupOpenResult } from "@finq/ui/types/popup"

definePageMeta({
  layoutProps: { hideNavbar: true, hideFooter: true, paddedIntercomIcon: true },
  middleware: ["hebrew-only"],
  metaData: "meta.pension.portfolio",
})

const kycTwoCookie = useCookie<boolean>("started-kyc-two", { default: () => false })
const isKycTwoOpen = ref(false)

const { t } = useI18n({ useScope: "local" })
const route = useRoute()
const { currentStage } = storeToRefs(usePensionFlowStageManager())
const { refetch: refetchPortfolioInfo, isLoading: isPortfolioInfoLoading } = useMyPortfolioInfo()
const { isLoading: isExclusionsLoading, userHasExclusions: isNoneHappyFlow } = useMyPensionExclusions()

const { data: recommendationsData, isLoading: isRecommendationsLoading } =
  useMyPensionRecommendations(undefined)

const isPageLoading = computed(() => {
  return isExclusionsLoading.value || isPortfolioInfoLoading.value || isRecommendationsLoading.value
})

const noRecommendationsFlow = computed(() => {
  return (
    recommendationsData.value?.length === 0 ||
    recommendationsData.value?.every((rec) => rec.transactions?.every((t) => t.action === "NO_CHANGE"))
  )
})

const isValuePropositionLocked = computed(() => {
  return currentStage.value === "PORTFOLIO"
})

const pageHeader = computed((): { title?: string; description?: string } => {
  switch (true) {
    // Non happy flow
    case isNoneHappyFlow.value:
      return {
        title: t("manualContinueProcess"),
      }

    case noRecommendationsFlow.value:
      return {
        title: t("noRecommendationsTitle"),
        description: t("noRecommendationsDescription"),
      }

    default:
      return {
        title: t("recommendationsHeader", { count: recommendationsData.value?.length || 0 }),
      }
  }
})

watch(
  currentStage,
  () => {
    if ("kyc" in route.query && currentStage.value === FlowStage.StageKey.PORTFOLIO) {
      kycTwoCookie.value = true
      isKycTwoOpen.value = true
    }

    // Refetch the portfolio info after fetching recommendations
    if (currentStage.value === FlowStage.StageKey.VALUE_PROPOSITION) {
      refetchPortfolioInfo()
    }
  },
  { immediate: true }
)

const login = useLogin()
const user = useUser()

watchEffect(() => {
  if (user.isAuthenticated.value) return
  login.open()
})

const popup = usePopup()
const popupInstance = ref<PopupOpenResult | null>(null)
const router = useRouterUtils()

// ------------------- Popup Handlers -------------------
const onSuccess = () => {
  popupInstance.value?.destroy()

  nextTick(() => {
    router.push({ name: "pension-recommendations" })
  })
}

const onClose = () => {
  popupInstance.value?.destroy()

  nextTick(() => {
    router.push({ name: "pension-portfolio" })
  })
}

function openCreditCardPopup() {
  popupInstance.value = popup.open({
    id: "payme-pensions-token",
    hideCloseButton: true,
    classes: {
      wrapper: cn("flex-1"),
      toolbar: cn("hidden"),
      modal: {
        root: "h-full flex-1",
        panel: cn("h-full max-w-none flex-1 overflow-y-auto overflow-x-hidden rounded-none p-0"),
      },
    },
    component: shallowRef(resolveComponent("KycPensionsRecommendationCheckout")),
    componentData: { onClose, onSuccess },
  })
}

// ------------------- Recommendation Flow -------------------
function handleRecommendationFlow() {
  if (kycTwoCookie.value || currentStage.value === FlowStage.StageKey.PORTFOLIO) {
    kycTwoCookie.value = true
    isKycTwoOpen.value = true
  } else if (noRecommendationsFlow.value) {
    router.push({ name: "kyc-questionnaire", params: { questionnaire: Kyc.QuestionnaireTypes.PENSIONS } })
  } else if (currentStage.value === FlowStage.StageKey.VALUE_PROPOSITION) {
    openCreditCardPopup()
  } else {
    router.push({ name: "pension-recommendations" })
  }
}
</script>

<i18n lang="json">
{
  "he": {
    "noRecommendationsTitle": "הפנסיה שלך במצב מעולה - כל הכבוד!",
    "noRecommendationsDescription": "ריכזנו לך את כל המוצרים הפנסיוניים במקום אחד, עם הסברים פשוטים ונגישים בגובה העיניים",
    "recommendationsHeader": "**{count} תובנות לשדרוג** המוצרים הפנסיונים שלך",
    "manualContinueProcess": "המשך תהליך וליווי אישי",
    "insightsDetails": "לפירוט התובנות שלי",
    "continue": "המשך",
    "pensionProductTitle": "המוצרים הפנסיוניים שלך"
  },
  "en": {}
}
</i18n>
